import { useCallback, useEffect, useState, type ReactNode } from "react";
import type { InviteLink } from "@omts/shared";
import { fmtDateTime } from "../../time";

export const errMsg = (e: unknown) => (e instanceof Error ? e.message : "Неизвестная ошибка");

/** Загрузка списка с перезагрузкой после действий. */
export function useList<T>(loader: () => Promise<T[]>) {
  const [items, setItems] = useState<T[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(() => {
    loader()
      .then((rows) => {
        setItems(rows);
        setError(null);
      })
      .catch((e: unknown) => setError(errMsg(e)));
  }, [loader]);
  useEffect(reload, [reload]);
  return { items, error, reload, setItems };
}

/** Поле формы в стиле NewLotPage: подпись, ошибка или подсказка. */
export function Field({
  label,
  error,
  hint,
  required,
  children,
}: {
  label: string;
  error?: string;
  hint?: ReactNode;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className={`field${error ? " invalid" : ""}`}>
      <span>
        {label}
        {required && <b className="req"> *</b>}
      </span>
      {children}
      {error ? <small className="field-error">{error}</small> : hint ? <small className="hint">{hint}</small> : null}
    </label>
  );
}

export function ActiveBadge({ active }: { active: boolean }) {
  return <span className={`badge${active ? "" : " muted"}`}>{active ? "активна" : "неактивна"}</span>;
}

export function TelegramTag({ linked, username }: { linked: boolean; username?: string | null }) {
  return linked ? (
    <span className="tag tg">подключён{username ? ` · @${username}` : ""}</span>
  ) : (
    <span className="tag warn">не подключён</span>
  );
}

/** Строка со ссылкой и кнопкой «Копировать». */
export function CopyField({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const el = document.getElementById("copy-field-input") as HTMLInputElement | null;
      el?.select();
      document.execCommand("copy");
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="copy-row">
      <input id="copy-field-input" readOnly value={value} onFocus={(e) => e.currentTarget.select()} />
      <button type="button" className="btn" onClick={() => void copy()}>
        {copied ? "Скопировано" : "Копировать"}
      </button>
    </div>
  );
}

/** Результат выпуска инвайт-ссылки: url + срок, или причина, почему ссылки нет. */
export function InviteResult({ invite, hint }: { invite: InviteLink; hint: string }) {
  if (!invite.url) return <p className="error">{invite.note ?? "Ссылка недоступна"}</p>;
  return (
    <>
      <CopyField value={invite.url} />
      <p className="hint">
        Ссылка одноразовая, действует до {fmtDateTime(invite.expiresAt)}. {hint}
      </p>
    </>
  );
}

/** Окно подтверждения опасного действия. */
export function ConfirmActions({
  busy,
  error,
  confirmLabel,
  danger,
  onCancel,
  onConfirm,
}: {
  busy: boolean;
  error: string | null;
  confirmLabel: string;
  danger?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <>
      {error && <p className="error">{error}</p>}
      <div className="modal-actions">
        <button type="button" className="btn" onClick={onCancel} disabled={busy}>
          Отмена
        </button>
        <button type="button" className={`btn ${danger ? "danger" : "primary"}`} onClick={onConfirm} disabled={busy}>
          {busy ? "…" : confirmLabel}
        </button>
      </div>
    </>
  );
}
